import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Truck, CreditCard, Building2, Banknote, Loader2, ShoppingBag, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { COURIERS, getCourierPrice, getCourierEta, FREE_SHIPPING_THRESHOLD_EUR } from '../lib/delivery';
import VatNotice, { isLikelyEuVatId } from '../components/VatNotice';
import TrustBadges from '../components/TrustBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, currency, getTotal, getCouponDiscount, coupon, clearCart } = useCart();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viesState, setViesState] = useState({ status: 'idle', data: null });
  
  const [formData, setFormData] = useState(() => {
    let detectedCountry = 'Slovakia';
    try {
      const stored = localStorage.getItem('sunify_geo_country_name');
      if (stored) detectedCountry = stored;
    } catch { /* ignore */ }
    return {
      customer_name: user?.name || '',
      customer_email: user?.email || '',
      customer_phone: '',
      delivery_method: 'gls',
      payment_method: 'stripe',
      address: {
        street: '',
        city: '',
        postal_code: '',
        country: detectedCountry,
        tax_id: ''
      },
      notes: ''
    };
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email
      }));
    }
  }, [user]);

  // Debounced VIES check whenever a syntactically-valid VAT ID is entered.
  useEffect(() => {
    const id = (formData.address.tax_id || '').trim();
    if (!id || !isLikelyEuVatId(id)) {
      setViesState({ status: 'idle', data: null });
      return;
    }
    setViesState({ status: 'checking', data: null });
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/vies/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vat_id: id }),
          signal: ctrl.signal,
        });
        if (!res.ok) {
          setViesState({ status: 'error', data: null });
          return;
        }
        const data = await res.json();
        setViesState({ status: data.valid ? 'valid' : 'invalid', data });
      } catch (e) {
        if (e.name !== 'AbortError') {
          setViesState({ status: 'error', data: null });
        }
      }
    }, 600);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [formData.address.tax_id]);

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 text-center">
          <ShoppingBag className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">{t('cart.empty')}</h1>
          <Link to="/catalog">
            <Button>{t('cart.continue_shopping')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [addressField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('sunify_token');
      
      // Prepare order items
      const orderItems = cart.map(item => ({
        product_id: item.id,
        product_name: item.translations?.sk?.name || item.name || 'Product',
        quantity: item.quantity,
        price_eur: item.price_eur,
        price_usd: item.price_usd
      }));

      const subtotalEur = cart.reduce((sum, item) => sum + item.price_eur * item.quantity, 0);
      const subtotalUsd = cart.reduce((sum, item) => sum + item.price_usd * item.quantity, 0);
      const discountPct = coupon?.discount_percent || 0;
      const discountEur = +(subtotalEur * discountPct / 100).toFixed(2);
      const discountUsd = +(subtotalUsd * discountPct / 100).toFixed(2);
      const cartTotalEur = +(subtotalEur - discountEur).toFixed(2);
      const cartTotalUsd = +(subtotalUsd - discountUsd).toFixed(2);
      const shippingEur = getCourierPrice(formData.delivery_method, formData.address.country, cartTotalEur);
      const shippingUsd = +(shippingEur * 1.1).toFixed(2); // approx. for USD orders
      const totalEur = +(cartTotalEur + shippingEur).toFixed(2);
      const totalUsd = +(cartTotalUsd + shippingUsd).toFixed(2);

      // Create order
      const orderResponse = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: orderItems,
          total_eur: totalEur,
          total_usd: totalUsd,
          currency: currency,
          delivery_method: formData.delivery_method,
          delivery_fee_eur: shippingEur,
          delivery_fee_usd: shippingUsd,
          delivery_address: formData.address,
          payment_method: formData.payment_method,
          customer_email: formData.customer_email,
          customer_name: formData.customer_name,
          customer_phone: formData.customer_phone,
          notes: formData.notes,
          coupon_code: coupon?.code || null,
          coupon_discount_percent: discountPct || null,
          discount_eur: discountEur,
          discount_usd: discountUsd
        })
      });

      if (!orderResponse.ok) {
        throw new Error('Failed to create order');
      }

      const order = await orderResponse.json();

      // Handle payment based on method
      if (formData.payment_method === 'stripe') {
        // Create Stripe checkout session
        const amount = currency === 'EUR' ? totalEur : totalUsd;
        const paymentResponse = await fetch(`${API_URL}/api/payments/checkout/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            order_id: order.id,
            amount: parseFloat(amount.toFixed(2)),
            currency: currency.toLowerCase(),
            origin_url: window.location.origin,
            customer_email: formData.customer_email
          })
        });

        if (!paymentResponse.ok) {
          throw new Error('Failed to create payment session');
        }

        const paymentData = await paymentResponse.json();
        
        // Redirect to Stripe
        window.location.href = paymentData.url;
      } else if (formData.payment_method === 'paypal') {
        // Create PayPal payment
        const amount = currency === 'EUR' ? totalEur : totalUsd;
        const paymentResponse = await fetch(`${API_URL}/api/payments/paypal/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            order_id: order.id,
            amount: parseFloat(amount.toFixed(2)),
            currency: currency.toUpperCase(),
            origin_url: window.location.origin
          })
        });

        if (!paymentResponse.ok) {
          throw new Error('Failed to create PayPal payment');
        }

        const paymentData = await paymentResponse.json();
        
        // Redirect to PayPal
        window.location.href = paymentData.approval_url;
      } else if (formData.payment_method === 'bank_transfer' || formData.payment_method === 'cash_on_delivery') {
        // For non-card payments, just clear cart and show success
        clearCart();
        navigate(`/checkout/success?order_id=${order.id}&method=${formData.payment_method}`);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'An error occurred during checkout');
    } finally {
      setLoading(false);
    }
  };

  const total = getTotal();
  const deliveryFee = getCourierPrice(formData.delivery_method, formData.address.country, total);
  const grandTotal = total + deliveryFee;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <Link to="/cart" className="inline-flex items-center text-orange-500 hover:text-orange-600 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('cart.title')}
        </Link>

        <h1 className="text-3xl font-bold mb-8" data-testid="checkout-title">{t('checkout.title')}</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Customer Info */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">{t('checkout.customer_info')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name">{t('contact.name')} *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      required
                      data-testid="checkout-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_email">{t('contact.email')} *</Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                      required
                      data-testid="checkout-email"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="customer_phone">{t('contact.phone')} *</Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      required
                      data-testid="checkout-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">{t('checkout.delivery')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="street">{t('checkout.street')} *</Label>
                    <Input
                      id="street"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      required
                      data-testid="checkout-street"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">{t('checkout.city')} *</Label>
                    <Input
                      id="city"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      required
                      data-testid="checkout-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">{t('checkout.postal_code')} *</Label>
                    <Input
                      id="postal_code"
                      name="address.postal_code"
                      value={formData.address.postal_code}
                      onChange={handleInputChange}
                      required
                      data-testid="checkout-postal"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">{t('checkout.country')} *</Label>
                    <select
                      id="country"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="checkout-country"
                    >
                      <option value="Slovakia">Slovakia</option>
                      <option value="Czech Republic">Czech Republic</option>
                      <option value="Germany">Germany</option>
                      <option value="Austria">Austria</option>
                      <option value="Poland">Poland</option>
                      <option value="Hungary">Hungary</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="tax_id">
                      {t('checkout.vat_id', 'IČ DPH / VAT ID')} <span className="text-gray-400 text-xs">({t('common.optional', 'voliteľné')})</span>
                    </Label>
                    <Input
                      id="tax_id"
                      name="address.tax_id"
                      value={formData.address.tax_id}
                      onChange={handleInputChange}
                      placeholder="SK2020123456 / DE123456789"
                      data-testid="checkout-vat-id"
                      className={
                        formData.address.tax_id && !isLikelyEuVatId(formData.address.tax_id)
                          ? 'border-red-300 focus:ring-red-500'
                          : ''
                      }
                    />
                    {formData.address.tax_id && !isLikelyEuVatId(formData.address.tax_id) && (
                      <p className="text-xs text-red-600 mt-1" data-testid="vat-id-format-error">
                        {t('checkout.vat_id_format', 'Formát: 2 písmená krajiny + 2-12 alfanumerických znakov (napr. SK2020123456).')}
                      </p>
                    )}
                    {formData.address.tax_id && isLikelyEuVatId(formData.address.tax_id) && (
                      <div className="mt-1 text-xs" data-testid="vies-status" data-vies-status={viesState.status}>
                        {viesState.status === 'checking' && (
                          <span className="text-gray-500">⏳ {t('checkout.vies_checking', 'Overujem VAT ID v VIES...')}</span>
                        )}
                        {viesState.status === 'valid' && (
                          <span className="text-emerald-700 font-medium" data-testid="vies-valid">
                            ✓ {t('checkout.vies_valid', 'VIES overené')}: <span className="font-semibold">{viesState.data?.name || '—'}</span>
                          </span>
                        )}
                        {viesState.status === 'invalid' && (
                          <span className="text-orange-600">
                            ⚠ {t('checkout.vies_invalid', 'VAT ID nenájdené v VIES — overí sa manuálne.')}
                          </span>
                        )}
                        {viesState.status === 'error' && (
                          <span className="text-gray-500">
                            ℹ {t('checkout.vies_error', 'VIES služba nedostupná, overíme manuálne.')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <VatNotice country={formData.address.country} vatId={formData.address.tax_id} />
                  </div>
                </div>

                {/* Delivery Method */}
                <h3 className="font-semibold mb-3">{t('checkout.delivery_method')}</h3>
                <div className="space-y-3" data-testid="courier-options">
                  {COURIERS.map((c) => {
                    const fee = getCourierPrice(c.id, formData.address.country, getTotal());
                    const eta = getCourierEta(c.id, formData.address.country);
                    const isFree = fee === 0;
                    const selected = formData.delivery_method === c.id;
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${selected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
                        data-testid={`courier-option-${c.id}`}
                      >
                        <input
                          type="radio"
                          name="delivery_method"
                          value={c.id}
                          checked={selected}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <Truck className="h-6 w-6 text-orange-500 mr-3" />
                        <div className="flex-grow">
                          <div className="font-medium">{c.name}</div>
                          <div className="text-sm text-gray-500">
                            {eta} {t('checkout.business_days')}
                          </div>
                        </div>
                        <div className="font-semibold text-right">
                          {isFree ? (
                            <span className="text-green-600">{t('checkout.free_shipping', 'Zdarma')}</span>
                          ) : (
                            <>€{fee.toFixed(2)}</>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-3" data-testid="free-shipping-note">
                  {t('checkout.free_shipping_note', { amount: FREE_SHIPPING_THRESHOLD_EUR })}
                </p>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">{t('checkout.payment')}</h2>
                <div className="space-y-3">
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${formData.payment_method === 'stripe' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="stripe"
                      checked={formData.payment_method === 'stripe'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <CreditCard className="h-6 w-6 text-orange-500 mr-3" />
                    <div className="flex-grow">
                      <div className="font-medium">{t('payment.stripe')}</div>
                      <div className="text-sm text-gray-500">Visa, Mastercard, AMEX</div>
                    </div>
                  </label>
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${formData.payment_method === 'paypal' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="paypal"
                      checked={formData.payment_method === 'paypal'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className="h-6 w-6 mr-3 flex items-center justify-center">
                      <span className="text-blue-600 font-bold text-sm">PP</span>
                    </div>
                    <div className="flex-grow">
                      <div className="font-medium">PayPal</div>
                      <div className="text-sm text-gray-500">{t('payment.paypal_desc')}</div>
                    </div>
                  </label>
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${formData.payment_method === 'bank_transfer' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="bank_transfer"
                      checked={formData.payment_method === 'bank_transfer'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <Building2 className="h-6 w-6 text-orange-500 mr-3" />
                    <div className="flex-grow">
                      <div className="font-medium">{t('payment.bank_transfer')}</div>
                      <div className="text-sm text-gray-500">{t('checkout.bank_transfer_note')}</div>
                    </div>
                  </label>
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${formData.payment_method === 'cash_on_delivery' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="cash_on_delivery"
                      checked={formData.payment_method === 'cash_on_delivery'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <Banknote className="h-6 w-6 text-orange-500 mr-3" />
                    <div className="flex-grow">
                      <div className="font-medium">{t('payment.cash_on_delivery')}</div>
                      <div className="text-sm text-gray-500">+€2.00</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">{t('checkout.notes')}</h2>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder={t('checkout.notes_placeholder')}
                  data-testid="checkout-notes"
                />
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-6 sticky top-24">
                <h2 className="text-xl font-semibold mb-4">{t('checkout.order_summary')}</h2>
                
                <div className="space-y-4 mb-6">
                  {cart.map(item => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <div>
                        <span className="font-medium">{item.translations?.sk?.name || item.name}</span>
                        <span className="text-gray-500 ml-2">x{item.quantity}</span>
                      </div>
                      <span>
                        {currency === 'EUR' ? '€' : '$'}
                        {((currency === 'EUR' ? item.price_eur : item.price_usd) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span>{t('cart.subtotal')}</span>
                    <span>
                      {currency === 'EUR' ? '€' : '$'}
                      {(total + getCouponDiscount()).toFixed(2)}
                    </span>
                  </div>
                  {coupon && getCouponDiscount() > 0 && (
                    <div className="flex justify-between text-orange-600" data-testid="checkout-coupon-row">
                      <span>
                        {t('cart.discount')} <span className="font-mono text-xs">({coupon.code} −{coupon.discount_percent}%)</span>
                      </span>
                      <span>
                        −{currency === 'EUR' ? '€' : '$'}{getCouponDiscount().toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between" data-testid="delivery-fee-row">
                    <span>{t('checkout.delivery')}</span>
                    <span>
                      {deliveryFee === 0 ? (
                        <span className="text-green-600">{t('checkout.free_shipping', 'Zdarma')}</span>
                      ) : (
                        <>{currency === 'EUR' ? '€' : '$'}{deliveryFee.toFixed(2)}</>
                      )}
                    </span>
                  </div>
                  {formData.payment_method === 'cash_on_delivery' && (
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>{t('payment.cash_on_delivery')} fee</span>
                      <span>€2.00</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>{t('cart.total')}</span>
                    <span className="text-orange-500">
                      {currency === 'EUR' ? '€' : '$'}
                      {(grandTotal + (formData.payment_method === 'cash_on_delivery' ? 2 : 0)).toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full mt-6 bg-orange-500 hover:bg-orange-600"
                  disabled={loading}
                  data-testid="checkout-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('checkout.place_order')
                  )}
                </Button>

                {/* Reassurance strip below the pay button */}
                <div className="mt-4">
                  <TrustBadges variant="compact" />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
